/** Шторка платных действий партии.
 *
 * Всё, что покупается прямо в партии, живёт в одном окне: для игрока это
 * одно решение — потратить кристаллы, чтобы стало легче, — и разносить
 * его по трём кнопкам значит заставить искать.
 *
 * Недоступный вариант не прячется и не молчит: он объясняет причину.
 * «Открыть клетку» без выбранной клетки бессмысленна, а не запрещена, и
 * разница между этими двумя вещами игроку важна.
 */

import type { HintKind } from '../api/types'
import { Sheet } from './Sheet'
import { BalanceChip, Crystal } from './ui'
import { t } from '../i18n'

/** Всё, что покупается в партии. */
export type SheetAction = HintKind | 'autonotes' | 'forgive'

export interface HintSheetProps {
  balance: number
  prices: Record<SheetAction, number>
  /** Бесплатная логическая подсказка: доступна или через сколько часов будет. */
  freeHint: { available: boolean; hours: number }
  /** Выбрана ли клетка — от этого зависит доступность «открыть клетку». */
  hasSelection: boolean
  /** Потолок награды за задачу дня — им объясняется, где взять кристаллы. */
  dailyReward: number
  busy?: boolean
  onPick: (action: SheetAction) => void
  onEarn: () => void
  onDismiss: () => void
}

export function HintSheet({
  balance,
  prices,
  freeHint,
  hasSelection,
  dailyReward,
  busy = false,
  onPick,
  onEarn,
  onDismiss,
}: HintSheetProps) {
  const shortfall = (price: number) => Math.max(0, price - balance)

  return (
    <Sheet title={t('hints.title')} right={<BalanceChip balance={balance} />} onDismiss={onDismiss}>
      <div className="stack">
        <Option
          title={t('hints.check')}
          hint={t('hints.checkHint')}
          price={prices.check}
          shortfall={shortfall(prices.check)}
          dailyReward={dailyReward}
          busy={busy}
          onPick={() => onPick('check')}
        />
        <Option
          title={t('hints.technique')}
          hint={t('hints.techniqueHint')}
          price={prices.technique}
          // Первая логическая подсказка в сутки бесплатна: иначе игрок так
          // и не узнаёт, что внутри объясняют приём, а не просто открывают
          // цифру. Знакомство должно быть бесплатным, продажи — потом.
          free={freeHint.available}
          waitHours={freeHint.available ? 0 : freeHint.hours}
          shortfall={freeHint.available ? 0 : shortfall(prices.technique)}
          dailyReward={dailyReward}
          busy={busy}
          onPick={() => onPick('technique')}
        />
        <Option
          title={t('hints.reveal')}
          hint={hasSelection ? t('hints.revealHint') : t('hints.revealNeedsCell')}
          price={prices.reveal}
          blocked={!hasSelection}
          shortfall={shortfall(prices.reveal)}
          dailyReward={dailyReward}
          busy={busy}
          onPick={() => onPick('reveal')}
        />
        <Option
          title={t('hints.autonotes')}
          hint={t('hints.autonotesHint')}
          price={prices.autonotes}
          shortfall={shortfall(prices.autonotes)}
          dailyReward={dailyReward}
          busy={busy}
          onPick={() => onPick('autonotes')}
        />
      </div>

      <button type="button" className="button" onClick={onEarn}>
        {t('hints.earn')}
      </button>
    </Sheet>
  )
}

interface OptionProps {
  title: string
  hint: string
  price: number
  shortfall: number
  dailyReward: number
  busy: boolean
  free?: boolean
  waitHours?: number
  /** Действие неприменимо по состоянию доски, а не по деньгам. */
  blocked?: boolean
  onPick: () => void
}

function Option({
  title,
  hint,
  price,
  shortfall,
  dailyReward,
  busy,
  free = false,
  waitHours = 0,
  blocked = false,
  onPick,
}: OptionProps) {
  const poor = !free && shortfall > 0
  const disabled = busy || blocked || poor

  // Причина недоступности важнее описания: игрок и так видит, что кнопка
  // не нажимается, — ему нужно знать, что с этим делать.
  const explanation = blocked
    ? hint
    : poor
      ? dailyReward > 0
        ? t('hints.notEnoughDaily', { amount: shortfall, reward: dailyReward })
        : t('hints.notEnough', { amount: shortfall })
      : !free && waitHours > 0
        ? t('hints.freeIn', { hours: waitHours })
        : hint

  return (
    <div className="achievement">
      <span className="list__grow">
        <span
          className="shop-item__title"
          style={{ display: 'block', color: disabled ? 'var(--disabled)' : undefined }}
        >
          {title}
        </span>
        <span className={blocked || poor ? 'error-text' : 'hint-text'}>{explanation}</span>
      </span>
      <button
        type="button"
        className={`button button--compact ${free ? 'button--free' : 'button--primary'}`}
        onClick={onPick}
        disabled={disabled}
      >
        {free ? t('hints.free') : <Crystal>{price}</Crystal>}
      </button>
    </div>
  )
}
